import { AssistantContext } from '~/steps/steps';
import { StepCategoryId } from '~/consts/assistant-steps';
import {
  FormField,
  FormMultipleChoiceComponentConfig,
  FormValueType,
  StepDefinition,
} from '@/interfaces/step-definition';
import { FormFieldType } from '@/consts/input-types';
import { CONTACT_DATA_FIELD_NAMES } from '~/steps/submit';
import { getFormattedFieldData } from '@/lib/formatter/context';
import i18n from '@/plugins/i18n';

const isEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

// Translate one MultipleChoice option value via the same i18n keys the
// comment formatter uses. Falls back to the raw option value if no
// translation key is registered.
const translateOption = (
  step: StepDefinition,
  field: FormField,
  optionValue: string,
): string => {
  const key = `${step.id}.formFields.${field.name}.options.${optionValue}.label`;
  return i18n.te(key) ? (i18n.t(key) as string) : optionValue;
};

const getTranslatedValue = (
  step: StepDefinition,
  field: FormField,
  rawValue: unknown,
  stepData: Record<string, unknown>,
  assistantContext: AssistantContext,
): unknown => {
  // MultipleChoice: translate each option. Multi-select fields stay as array
  // (["Wärmepumpe","Wallbox"]). Single-answer fields (singleAnswer: true) get
  // unwrapped to a plain string — they're conceptually "pick one", not "pick
  // many", even though the form stores them as a one-element array.
  if (field.type === FormFieldType.MultipleChoice && Array.isArray(rawValue)) {
    const translated = rawValue.map((v) =>
      translateOption(step, field, String(v)),
    );
    const singleAnswer = (field.component as FormMultipleChoiceComponentConfig)
      ?.singleAnswer;
    return singleAnswer ? translated[0] : translated;
  }
  // Everything else (TextField, Slider, Checkbox, Switch, ...): defer to the
  // formatter chain — including the step's outputFormatter — so e.g. the
  // roofDirection slider gets mapped from "3" to "Süden" via its tickLabels.
  const formatted = getFormattedFieldData(
    field,
    assistantContext,
    stepData as { [key: string]: FormValueType },
    step,
  );
  return formatted.formattedValue;
};

export const getRequestAssistantData = (
  steps: StepDefinition[],
  assistantContext: AssistantContext,
  categories: StepCategoryId[],
): Record<string, unknown> => {
  const data: Record<string, unknown> = {};

  // Built inside the function body (not at module level) on purpose:
  // submit/index.ts imports this file, and reading CONTACT_DATA_FIELD_NAMES
  // during module evaluation would hit a temporal-dead-zone on the cycle.
  // At call time, both modules are fully initialized.
  const topLevelContactFields = new Set<string>(CONTACT_DATA_FIELD_NAMES);

  const relevantSteps = steps.filter(
    (step) =>
      categories.includes(step.categoryId as StepCategoryId) &&
      step.fields &&
      step.fields.length > 0 &&
      !(step.isDisabled && step.isDisabled(assistantContext)),
  );

  relevantSteps.forEach((step) => {
    const stepData = (
      assistantContext as unknown as Record<string, Record<string, unknown>>
    )[step.id];
    if (!stepData) return;

    step.fields?.forEach((field) => {
      if (topLevelContactFields.has(field.name)) return;
      const value = stepData[field.name];
      if (isEmpty(value)) return;
      data[field.name] = getTranslatedValue(
        step,
        field,
        value,
        stepData,
        assistantContext,
      );
    });
  });

  return data;
};
